import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";

export default function ChampionWall() {
    const [champs, setChamps] = useState([]);
    useEffect(() => { api.get("/champions").then(r => setChamps(r.data)).catch(() => {}); }, []);

    return (
        <div className="pt-[68px]" data-testid="champions-page">
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-16">
                <div className="overline mb-6">[ Legacy ] The wall</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.86] text-5xl md:text-[9vw]">
                    <span className="text-volt">Champions</span>
                </h1>
                <p className="mt-8 text-secondary max-w-xl text-base md:text-lg leading-relaxed">
                    Every state champion. Every game. Every year. Names carved in the circuit — permanent record, no revisions.
                </p>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
                {champs.length === 0 ? (
                    <div className="border border-white/10 clip-frame-sm p-16 text-center">
                        <div className="overline text-white/40 mb-3">Wall unveiling</div>
                        <div className="font-display text-2xl">First state champions crowned soon.</div>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                        {champs.map((c, i) => <ChampionCard key={i} c={c} i={i} />)}
                    </div>
                )}

                {/* Timeline note */}
                <div className="mt-24 border-t border-white/10 pt-10 grid md:grid-cols-3 gap-8">
                    <div>
                        <div className="overline mb-3">[ 01 ]</div>
                        <div className="font-display font-extrabold text-xl leading-tight">Two crowns per state</div>
                        <p className="mt-3 text-secondary text-sm leading-relaxed">Every state produces exactly two champions — one Warzone squad, one MW4 Multiplayer team.</p>
                    </div>
                    <div>
                        <div className="overline mb-3">[ 02 ]</div>
                        <div className="font-display font-extrabold text-xl leading-tight">Names live here forever</div>
                        <p className="mt-3 text-secondary text-sm leading-relaxed">Rosters are archived on this page permanently. No edits, no removals.</p>
                    </div>
                    <div>
                        <div className="overline mb-3">[ 03 ]</div>
                        <div className="font-display font-extrabold text-xl leading-tight">National stage awaits</div>
                        <p className="mt-3 text-secondary text-sm leading-relaxed">Every champion listed here has earned a seat at the eventual national final.</p>
                    </div>
                </div>
            </section>
        </div>
    );
}

const ChampionCard = ({ c, i }) => (
    <motion.article
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
        className="relative border border-white/10 clip-frame bg-[#0A0A0A] overflow-hidden card-lift p-8"
        data-testid={`champion-${c.state}-${c.game.replace(/[^A-Z]/g,'')}`}
    >
        {/* Watermark */}
        <img src="/brand/watermark.png" alt="" className="absolute -right-8 -bottom-8 w-64 opacity-[0.05] pointer-events-none" />

        <div className="flex items-start justify-between mb-6 relative">
            <div>
                <div className="overline">{c.game}</div>
                <div className="mt-2 font-mono text-xs text-white/40 tracking-[0.24em] uppercase">{c.state_name} · {c.year}</div>
            </div>
            <div className="text-right">
                <div className="font-display font-black text-volt tracking-tighter text-2xl leading-none">★</div>
                <div className="mt-2 font-mono text-[10px] text-white/40 tracking-[0.22em] uppercase">Champion</div>
            </div>
        </div>

        <h3 className="font-display font-black tracking-tighter text-3xl md:text-4xl leading-[0.95] relative">{c.team_name}</h3>
        <div className="mt-2 font-mono text-xs text-volt tracking-[0.24em]">[ {c.team_tag} ]</div>

        <div className="mt-8 grid grid-cols-2 gap-6 pt-6 border-t border-white/5 relative">
            <div>
                <div className="overline text-white/40 mb-2">Roster</div>
                <ul className="space-y-1">
                    {c.roster.map(r => <li key={r} className="text-sm text-white/85">/{r}</li>)}
                </ul>
            </div>
            <div>
                <div className="overline text-white/40 mb-2">Prize</div>
                <div className="font-display font-black text-3xl text-white">${c.prize.toLocaleString()}</div>
                <div className="mt-6 overline text-white/40 mb-1">Runner-up</div>
                <div className="text-sm text-white/60">{c.runner_up}</div>
            </div>
        </div>
    </motion.article>
);
